import {Order} from 'webapp-lib/typings';

import {
  assertUnreachable,
  PaginationArguments,
  PaginationDirection,
  PaginationMode,
  PaginationResponse,
} from '_/utilities/types';

export const mapPageItems = <T, R>(
  page: PaginationResponse<T>,
  itemMapper: (item: T) => R
): PaginationResponse<R> => ({
  hasMoreBefore: page.hasMoreBefore,
  items: page.items.map((item) => itemMapper(item)),
  hasMoreAfter: page.hasMoreAfter,
});

/**
 * Merges lazily loaded data by either *prepended* or
 * *appended* newly loaded data depending on the *direction*.
 */
export const updateWindow = <T>(
  oldWindow: PaginationResponse<T>,
  paginationArguments: PaginationArguments,
  serverPage: PaginationResponse<T>
): PaginationResponse<T> => {
  const oldItems = oldWindow.items;
  const newItems = serverPage.items;

  const browsing = [PaginationMode.Browse, undefined].includes(
    paginationArguments.mode
  );

  if (!browsing || !paginationArguments.cursor) {
    return {
      hasMoreBefore: serverPage.hasMoreBefore,
      items: newItems,
      hasMoreAfter: serverPage.hasMoreAfter,
    };
  }

  switch (paginationArguments.direction) {
    case undefined: // falls through
    case PaginationDirection.Forward: {
      const toBeItems = [...oldItems, ...newItems];
      return {
        hasMoreBefore: oldWindow.hasMoreBefore,
        items: toBeItems,
        hasMoreAfter: serverPage.hasMoreAfter,
      };
    }
    case PaginationDirection.Backward: {
      const toBeItems = [...newItems, ...oldItems];
      return {
        hasMoreBefore: serverPage.hasMoreBefore,
        items: toBeItems,
        hasMoreAfter: oldWindow.hasMoreAfter,
      };
    }
    default:
      assertUnreachable(paginationArguments.direction);
  }
};

export const orderMap: Record<Order, 'ASC' | 'DESC'> = {
  [Order.Ascending]: 'ASC',
  [Order.Descending]: 'DESC',
};
